import { computed, type MaybeRef, unref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";

interface UseAdminOrganizationWalletLevelOptions {
  organizationId: MaybeRef<number | null | undefined>;
  enabled?: MaybeRef<boolean>;
}

function isPositiveId(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

export function useAdminOrganizationWalletLevel(options: UseAdminOrganizationWalletLevelOptions) {
  const api = useApi();
  const organizationId = computed(() => unref(options.organizationId));
  const enabled = computed(() => (unref(options.enabled) ?? true) && isPositiveId(organizationId.value));

  const query = useQuery({
    queryKey: computed(() => ["admin", "wallet", "organization", organizationId.value, "level"]),
    queryFn: async () => {
      if (!isPositiveId(organizationId.value)) {
        throw new Error("organizationId is required to load organization wallet level");
      }

      return await api.admin.wallet.getOrganizationWalletLevel({
        params: { organization: organizationId.value },
      });
    },
    enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });

  const progress = computed(() => query.data.value ?? null);

  return {
    progress,
    currentLevel: computed(() => progress.value?.current_level ?? null),
    nextLevel: computed(() => progress.value?.next_level ?? null),
    hasNextLevel: computed(() => progress.value?.next_level !== null),
    lifetimeGrossCents: computed(() => progress.value?.lifetime_gross_cents ?? 0),
    remainingGrossCents: computed(() => progress.value?.remaining_gross_cents ?? null),
    currency: computed(() => progress.value?.currency ?? null),
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    query,
  };
}
